import {
   Pagination,
   PaginationContent,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
   calculatePageRange,
} from '@ifixit/ui/components/pagination';
import { cn } from '@ifixit/ui/lib/utils';
import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * The pager under a dashboard's list. The caller owns the URL: `pageHref`
 * names everything a copied or new-tab link needs to land on the same
 * view (its filter, its tab), while a plain click stays client-side
 * through `onPageChange`.
 */
export function DashboardPagination({
   page,
   pageCount,
   pageHref,
   onPageChange,
}: {
   page: number;
   pageCount: number;
   pageHref: (page: number) => string;
   onPageChange: (page: number) => void;
}) {
   if (pageCount <= 1) {
      return null;
   }
   const range = calculatePageRange(page, pageCount, 5);
   const pages = [];
   for (let i = range.start; i <= range.end; i++) {
      pages.push(i);
   }
   const goTo = (event: ReactMouseEvent, target: number) => {
      // Modified clicks keep the native link behavior (new tab/window)
      // via the real href.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
         return;
      }
      event.preventDefault();
      if (target >= 1 && target <= pageCount && target !== page) {
         onPageChange(target);
      }
   };
   return (
      <div className="mt-4">
         <Pagination>
            <PaginationContent className="list-none pl-0">
               <PaginationItem>
                  <PaginationPrevious
                     href={pageHref(page - 1)}
                     aria-disabled={page === 1}
                     className={cn('no-underline', page === 1 && 'pointer-events-none opacity-50')}
                     onClick={event => goTo(event, page - 1)}
                  />
               </PaginationItem>
               {pages.map(target => (
                  <PaginationItem key={target}>
                     <PaginationLink
                        href={pageHref(target)}
                        isActive={target === page}
                        className="no-underline"
                        onClick={event => goTo(event, target)}
                     >
                        {target}
                     </PaginationLink>
                  </PaginationItem>
               ))}
               <PaginationItem>
                  <PaginationNext
                     href={pageHref(page + 1)}
                     aria-disabled={page === pageCount}
                     className={cn(
                        'no-underline',
                        page === pageCount && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, page + 1)}
                  />
               </PaginationItem>
            </PaginationContent>
         </Pagination>
      </div>
   );
}
